/*
 * ct-pdp-upsell.js | Cosmetique Totale
 * "Maak je routine compleet" (snippets/ct-pdp-upsell.liquid): eindeloze rij
 * (David, 29-10).
 *
 * - Eindeloos in beide richtingen zonder kopieën in de DOM: kaarten die uit
 *   beeld raken verhuizen onzichtbaar naar de andere kant. In rust staat er
 *   precies één kaart links buiten beeld (scrollLeft = 1 kaartbreedte), zodat
 *   ook terugbladeren altijd kan. Vereist minstens ~5 kaarten; met minder
 *   loopt de rij gewoon terug naar het begin/eind.
 * - Pijlen: één tegelijk zichtbaar, in de richting waarin je bladert
 *   (data-dir="next" -> rechterpijl, "prev" -> linkerpijl). Volgt klikken,
 *   slepen, swipen en scrollen. Fade via CSS.
 * - Desktop: rij vastpakken en slepen met de muis (grab). Bij loslaten naar
 *   de dichtstbijzijnde kaart, met een zetje in de sleeprichting. Een sleep
 *   triggert geen klik op kaart of Toevoegen-knop.
 * - Snel klikken: de lopende stap wordt eerst direct afgerond.
 * Kaarten komen later binnen (ct-pdp.js sectie 6, track.innerHTML).
 * Stijl: assets/ct-pdp-upsell.css.
 */
if (!customElements.get('ct-upsell-loop')) {
  customElements.define('ct-upsell-loop', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ct-scroll-track]');
      this.prevBtn = this.querySelector('[data-ct-loop-prev]');
      this.nextBtn = this.querySelector('[data-ct-loop-next]');
      if (!this.track || !this.prevBtn || !this.nextBtn) return;

      this.dataset.dir = 'next';
      this.target = null;
      this.timer = null;
      this.dragging = false;
      this.lastLeft = 0;

      this.prevBtn.addEventListener('click', () => this.go(-1));
      this.nextBtn.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      ['wheel', 'touchstart'].forEach((ev) => this.track.addEventListener(ev, () => this.cancelStep(), { passive: true }));
      this.initDrag();

      // Nieuwe kaarten van buitenaf (eigen verplaatsingen worden weggefilterd)
      this.mo = new MutationObserver(() => { this.primed = false; this.setup(); });
      this.mo.observe(this.track, { childList: true });
      this.ro = new ResizeObserver(() => this.setup());
      this.ro.observe(this.track);
      this.setup();
    }

    disconnectedCallback() {
      this.mo?.disconnect();
      this.ro?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.idle);
    }

    step() {
      const card = this.track.firstElementChild;
      if (!card) return 0;
      const style = getComputedStyle(this.track);
      return card.getBoundingClientRect().width + (parseFloat(style.columnGap || style.gap) || 0);
    }

    maxLeft() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    setup() {
      const w = this.step();
      const overflow = this.track.children.length > 1 && this.maxLeft() > 2;
      this.toggleAttribute('data-static', !overflow);
      this.canLoop = overflow && w > 0 && this.maxLeft() >= 2 * w - 2;
      if (this.canLoop && !this.primed) {
        this.primed = true;
        // Eén kaart links buiten beeld klaarzetten; zichtbaar blijft kaart 1
        this.instant(() => {
          this.track.prepend(this.track.lastElementChild);
          this.track.scrollLeft = w;
        });
      }
      this.lastLeft = this.track.scrollLeft;
    }

    // Scrollpositie/volgorde aanpassen zonder animatie, snap en richtingwissel
    instant(fn) {
      const t = this.track;
      t.style.scrollSnapType = 'none';
      t.style.scrollBehavior = 'auto';
      fn();
      this.lastLeft = t.scrollLeft;
      this.mo?.takeRecords();
      cancelAnimationFrame(this.snapFrame);
      this.snapFrame = requestAnimationFrame(() => {
        if (this.dragging) return;
        t.style.scrollSnapType = '';
        t.style.scrollBehavior = '';
      });
    }

    // In rust: scrollLeft tussen 1 en 2 kaartbreedtes houden
    normalize() {
      if (!this.canLoop) return;
      const t = this.track;
      const w = this.step();
      if (!w) return;
      this.instant(() => {
        let guard = t.children.length;
        while (t.scrollLeft >= 2 * w - 2 && guard-- > 0) {
          t.append(t.firstElementChild);
          t.scrollLeft -= w;
        }
        guard = t.children.length;
        while (t.scrollLeft < w - 2 && guard-- > 0) {
          t.prepend(t.lastElementChild);
          t.scrollLeft += w;
        }
      });
    }

    cancelStep() {
      clearTimeout(this.timer);
      this.timer = null;
      this.target = null;
    }

    // Lopende stap direct afronden en bijdraaien
    finishStep() {
      clearTimeout(this.timer);
      this.timer = null;
      if (this.target !== null) {
        const to = this.target;
        this.target = null;
        this.instant(() => { this.track.scrollLeft = to; });
      }
      this.normalize();
    }

    animateTo(to) {
      this.target = Math.max(0, Math.min(to, this.maxLeft()));
      this.track.scrollTo({ left: this.target, behavior: 'smooth' });
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.finishStep(), 500);
    }

    go(dir) {
      this.finishStep();
      this.dataset.dir = dir > 0 ? 'next' : 'prev';
      const t = this.track;
      const w = this.step();
      const max = this.maxLeft();
      if (!w || max < 2) return;
      if (!this.canLoop) {
        // Te weinig kaarten om te draaien: aan het eind terug naar het begin
        if (dir > 0 && t.scrollLeft >= max - 2) return this.animateTo(0);
        if (dir < 0 && t.scrollLeft <= 2) return this.animateTo(max);
      }
      this.animateTo(t.scrollLeft + dir * w);
    }

    // Richting uit scrollen/swipen (niet tijdens eigen stappen of slepen)
    onScroll() {
      const left = this.track.scrollLeft;
      const delta = left - this.lastLeft;
      this.lastLeft = left;
      if (this.dragging || this.target !== null || Math.abs(delta) < 2) return;
      this.dataset.dir = delta > 0 ? 'next' : 'prev';
      clearTimeout(this.idle);
      this.idle = setTimeout(() => {
        if (!this.dragging && this.target === null) this.normalize();
      }, 160);
    }

    initDrag() {
      const t = this.track;
      let id = null;
      let startX = 0;
      let lastX = 0;
      let startLeft = 0;
      let moved = false;
      let dx = 0;

      t.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || this.hasAttribute('data-static')) return;
        this.finishStep();
        id = e.pointerId;
        startX = lastX = e.clientX;
        startLeft = t.scrollLeft;
        moved = false;
        dx = 0;
        this.dragging = true;
      });

      t.addEventListener('pointermove', (e) => {
        if (!this.dragging || e.pointerId !== id) return;
        dx = e.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) < 6) return;
          moved = true;
          t.setPointerCapture(id);
          t.style.scrollSnapType = 'none';
          t.style.scrollBehavior = 'auto';
          this.classList.add('is-dragging');
        }
        if (Math.abs(e.clientX - lastX) >= 2) {
          // muis naar links = verder in de rij = rechterpijl
          this.dataset.dir = e.clientX < lastX ? 'next' : 'prev';
          lastX = e.clientX;
        }
        t.scrollLeft = startLeft - dx;
        if (this.canLoop) {
          const w = this.step();
          if (t.scrollLeft < 2) {
            t.prepend(t.lastElementChild);
            t.scrollLeft += w;
            startLeft += w;
          } else if (t.scrollLeft > this.maxLeft() - 2) {
            t.append(t.firstElementChild);
            t.scrollLeft -= w;
            startLeft -= w;
          }
          this.mo.takeRecords();
        }
        this.lastLeft = t.scrollLeft;
      });

      const end = (e) => {
        if (!this.dragging || e.pointerId !== id) return;
        this.dragging = false;
        if (!moved) return;
        this.classList.remove('is-dragging');
        if (t.hasPointerCapture(id)) t.releasePointerCapture(id);
        this.suppressClick = true;
        setTimeout(() => { this.suppressClick = false; }, 0);
        const w = this.step();
        const pos = w ? t.scrollLeft / w : 0;
        const index = dx < -30 ? Math.ceil(pos - 0.02) : dx > 30 ? Math.floor(pos + 0.02) : Math.round(pos);
        t.style.scrollSnapType = '';
        t.style.scrollBehavior = '';
        this.animateTo(index * w);
      };
      t.addEventListener('pointerup', end);
      t.addEventListener('pointercancel', end);

      // Na een sleep geen klik op kaart of knop
      t.addEventListener('click', (e) => {
        if (!this.suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
      }, true);
      t.addEventListener('dragstart', (e) => e.preventDefault());
    }
  });
}
